import { Delete } from '@mui/icons-material';
import { Box, Stack, useMediaQuery } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DrugReferencesSearch from './DrugReferencesSearch';
import { getDrugReferencesColumns } from './DrugReferencesTableColumns';
import SingleTenantSelector from './SingleTenantSelector';
import { AdministrationRouteWorkflow, DrugReferencesTableProps } from './types';
import WorkflowSelector from './WorkflowSelector';
import ModalConfirmation from '../Calendar/ModalConfirmation';
import { pushSnackbar } from 'FRONTEND/actions/app';
import { RootState } from 'FRONTEND/reducers/types';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import { VidalDrugReference, useAddDrugReferenceMutation, useDeleteDrugReferenceMutation, useEditDrugReferenceMutation, useListDrugReferencesQuery } from 'FRONTEND/utils/backendApi/drugReferencesApi';
import i18n from 'FRONTEND/utils/i18n';
import { RIGHTS } from 'FRONTEND/utils/rights';

// Large initial page size so we can disable pagination. Easily changeable in the future if needed.
// NOTE: Max page size for DataGrid is 100, for more we need DataGridPro(paid version).
// DataGridPro also allows disabling pagination entirely (which is what we wanted ideally), but for now this is enought in our use case.
const paginationModel = { page: 0, pageSize: 100 };

const gridLocaleMap = {
  fr: frFR.components.MuiDataGrid.defaultProps.localeText,
  en: enUS.components.MuiDataGrid.defaultProps.localeText,
};

const DrugReferencesTable = ({
  workflowsWithRoutes,
}: DrugReferencesTableProps) => {
  const dispatch = useDispatch();
  const lang = useSelector((state: RootState) => state.user.locale);
  const isSmallScreen = useMediaQuery('(max-width:900px)');
  const [selectedTenant, setSelectedTenant] = useState<string>('');

  // Safe to cast as an array since we are fully commiting to the [{value, label}] (or [string] if not translation) notation for choices
  const [selectedWorkflow, setSelectedWorkflow] = useState<AdministrationRouteWorkflow | null>(workflowsWithRoutes && workflowsWithRoutes.length > 0 ? workflowsWithRoutes[0] : null);
  const choices = selectedWorkflow?.administrationRoutes?.choices as unknown as ({value: string, label: string} | string)[];

  // Rights
  const searchRight = useSelector((state: RootState) => selectHasRight(state, RIGHTS.DRUG_REFERENCES_SEARCH));
  const addRight = useSelector((state: RootState) => selectHasRight(state, RIGHTS.DRUG_REFERENCES_CREATE));
  const editRight = useSelector((state: RootState) => selectHasRight(state, RIGHTS.DRUG_REFERENCES_UPDATE));
  const deleteRight = useSelector((state: RootState) => selectHasRight(state, RIGHTS.DRUG_REFERENCES_DELETE));

  // Delete Modal
  const [referenceToDelete, setReferenceToDelete] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const handleDeleteModalClose = (confirmed: boolean) => {
    if (confirmed && referenceToDelete) {
      handleDeleteDrugReference({ tenantId: selectedTenant, drugReferenceId: referenceToDelete });
    } else {
      setReferenceToDelete(null);
    }
    setIsDeleteModalOpen(false);
  };

  // API Management
  // NOTE: `currentData` (not `data`) is used so that it clears to undefined immediately when selectedTenant changes,
  // rather than retaining the previous tenant's results until the new fetch completes.
  const { currentData: storedReferencesForTenant, isFetching: isLoadingStoredReferences } = useListDrugReferencesQuery({ tenantId: selectedTenant, includeDeleted: false }, {
    skip: !selectedTenant, // Skip the query if no tenant is selected.
  });
  const [addDrugReference, { isLoading: isAdding }] = useAddDrugReferenceMutation();
  const [editDrugReference, { isLoading: isEditing }] = useEditDrugReferenceMutation();
  const [deleteDrugReference, { isLoading: isDeleting }] = useDeleteDrugReferenceMutation();

  const handleAddDrugReference = (vidal_id: string, vidal_type: VidalDrugReference['type']) => {
    if (!addRight) {
      return;
    }
    addDrugReference({ tenantId: selectedTenant, vidal_id, vidal_type }).unwrap()
      .then(() => {
        dispatch(pushSnackbar({ message: i18n._('Drug added'), variant: 'success' }));
      })
      .catch(() => {
        dispatch(pushSnackbar({ message: i18n._('Error adding drug. Please try again.'), variant: 'error' }));
      });
  };

  const handleEditDrugReference = (params: { drugReferenceId: string, preferredRoute: string }) => {
    if (!editRight) {
      return;
    }
    return editDrugReference(params).unwrap().catch(() => {
      dispatch(pushSnackbar({ message: i18n._('Error editing drug. Please try again.'), variant: 'error' }));
    });
  };

  const handleDeleteDrugReference = (params: { tenantId: string, drugReferenceId: string }) => {
    if (!deleteRight) {
      return;
    }
    return deleteDrugReference(params).unwrap()
      .then(() => {
        dispatch(pushSnackbar({ message: i18n._('Drug deleted'), variant: 'success' }));
      })
      .catch(() => {
        dispatch(pushSnackbar({ message: i18n._('Error deleting drug. Please try again.'), variant: 'error' }));
      });
  };

  // Early return if no available workflows
  if (!workflowsWithRoutes || workflowsWithRoutes.length === 0) {
    return (
      <Box sx={{ padding: 4, textAlign: 'center' }}>
        {i18n._('No workflows available')}
      </Box>
    );
  }

  // Columns moved to a factory function in a separate file for better readability.
  const columns = getDrugReferencesColumns({
    isEditing,
    editRight,
    deleteRight,
    choices,
    handleEditDrugReference,
    setReferenceToDelete,
    setIsDeleteModalOpen,
    userLocale: lang,
  });

  const Slot = ({ children, color }: {children: React.ReactNode, color?: string}) => {
    return (
      <Box style={{ padding: '20px', textAlign: 'center', color: color || 'inherit' }}>
        {children}
      </Box>
    );
  };

  // Modify what is displayed instead of rows (Errors, loading state, etc..)
  const slots = {
    noRowsOverlay: () => <Slot color='warning'>{i18n._('The endowment list is empty. Add therapies to fill the list.')}</Slot>,
  };

  const isPaginationDisabled = (storedReferencesForTenant?.length || 0) <= paginationModel.pageSize;

  return (
    <Stack sx={{ paddingX: isSmallScreen ? 1 : '10%' }}>
      <Stack direction='row' justifyContent='space-between' py={2}>
        <DrugReferencesSearch
          onMedicineSelected={handleAddDrugReference}
          disabled={!searchRight}
        />
        <Stack direction='row' spacing={1} alignItems='center'>
          {workflowsWithRoutes && workflowsWithRoutes.length > 1 && <WorkflowSelector workflowsWithRoutes={workflowsWithRoutes} onWorkflowSelected={(wf) => setSelectedWorkflow(wf)} />}
          <SingleTenantSelector
            onTenantSelected={(tenantId) => setSelectedTenant(tenantId)}
            neededRights={[
              RIGHTS.DRUG_REFERENCES_SEARCH,
              RIGHTS.DRUG_REFERENCES_LIST,
            ]} // Only show tenants with drug reference search rights.
          />
        </Stack>
      </Stack>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', pb: 4 }} // replaces "autoHeight" prop on DataGrid (deprecated). This is recommended by x-data-grid.
      >
        <DataGrid
          columns={columns}
          localeText={gridLocaleMap[lang]}
          loading={isLoadingStoredReferences || isDeleting || isAdding}
          rows={storedReferencesForTenant || []}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus':
            {
              outline: 'none',
            },
          }}
          initialState={{
            pagination: { paginationModel },
            sorting: {
              sortModel: [{ field: 'name', sort: 'asc' }],
            },
          }}
          slots={slots}
          hideFooterPagination={isPaginationDisabled}
        />
      </Box>
      <ModalConfirmation
        open={isDeleteModalOpen}
        message={i18n._('Are you sure you want to delete this medicine?')}
        onClose={handleDeleteModalClose}
        title={<Stack direction='row' alignItems='center' gap={1}><Delete /> {i18n._('Confirm')}</Stack>}
      />
    </Stack>
  );
};
export default DrugReferencesTable;
